/**
 * The blueprint builder's wire types, transcribed from the contract in pm.api
 * `documentation/BLUEPRINT_BUILDER_API.md` (MVP, 2026-09-28), plus the v2
 * additions (data coverage, AI Curated rows on save). Field names are the
 * contract's, unchanged; where the contract leaves a value's emptiness open (a
 * job with no error, an issue with no landing) the type admits null so the
 * screens have to handle it. The v2 fields are optional because versions and
 * saves stored before v2 do not carry them.
 *
 * Ids are integers except job ids (UUID strings). Dates are ISO `YYYY-MM-DD`,
 * timestamps ISO 8601 UTC.
 */

export type IsoDate = string;
export type IsoTimestamp = string;

/* ------------------------------------------------------------------ *
 * Jobs, threads and messages
 * ------------------------------------------------------------------ */

export type JobStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'refused'
  | 'canceled'
  | 'lost';

export type Step = {
  tool: string;
  summary: string;
  ok: boolean;
};

export type Job = {
  id: string;
  kind: string;
  status: JobStatus;
  error: { message?: string } | null;
  refusal: { message?: string } | null;
  created_at: IsoTimestamp;
  finished_at: IsoTimestamp | null;
  /**
   * NOT in the contract. A backend that reports the running turn's steps on
   * the job itself may send them here; the running indicator reads them
   * first and falls back to the in-progress assistant message.
   */
  steps?: Step[];
};

export type Constraints = {
  /**
   * Empty, or absent, until the user or the builder picks some: a new chat
   * no longer starts from a default book.
   */
  instruments?: string[] | null;
  weeks: number;
  years: [number, number];
  character: string | null;
  news_per_turn: number;
  boost_terms: string[];
};

export type ProposalRef = {
  proposal_id: number;
  version_id: number;
};

export type MessageRole = 'user' | 'assistant' | 'error';

/** A quick reply to a clarifying question. */
export type QuestionOption = {
  /** The button's text. */
  label: string;
  /** What clicking it sends, as an ordinary user message. */
  message: string;
};

/** A clarifying question the builder asks; the user may also answer in free text. */
export type Question = {
  text: string;
  options: QuestionOption[];
};

export type Message = {
  id: number;
  role: MessageRole;
  text: string;
  created_at: IsoTimestamp;
  job_id: string | null;
  steps: Step[];
  proposal_refs: ProposalRef[];
  /** An assistant message's clarifying question. Absent on older messages. */
  question?: Question | null;
  /**
   * The data gaps of the proposals an assistant message shows. They replace
   * the "Data gaps:" block the backend used to append to the text; absent on
   * older messages, whose text still carries that block.
   */
  gaps?: Gap[] | null;
};

export type ThreadSummary = {
  id: number;
  title: string;
  created_at: IsoTimestamp;
  updated_at: IsoTimestamp;
  proposal_count: number;
  active_job: Job | null;
};

export type ThreadDetail = ThreadSummary & {
  constraints: Constraints;
  messages: Message[];
  proposals: ProposalCard[];
};

export type ThreadList = { results: ThreadSummary[] };

export type CreateThreadResponse = {
  thread: ThreadDetail;
  job: Job | null;
};

export type SendMessageResponse = {
  message: Message;
  job: Job;
};

/* ------------------------------------------------------------------ *
 * Proposals
 * ------------------------------------------------------------------ */

export type ProposalStatus = 'open' | 'saved' | 'rejected';
export type CheckStatus = 'ok' | 'warn' | 'fail';
export type MoveUnit = '%' | 'bp';

/** A cumulative move over the proposal's window. */
export type Move = {
  code: string;
  value: number | null;
  unit: MoveUnit;
};

export type Overlap = {
  blueprint_id: number;
  name: string;
  /** Share of this window the blueprint covers, 0–1. */
  fraction: number;
  cadence_days: number | null;
  same_cadence: boolean;
  visible: boolean;
  status: string;
};

export type ProposalWindow = {
  start: IsoDate;
  end: IsoDate;
  weeks: number;
};

/**
 * Whether the preview's data can carry the proposal: `blocked` when a gap
 * would make the save fail or mis-grade a traded instrument, `partial` when
 * there are only warnings, `complete` otherwise.
 */
export type DataStatus = 'complete' | 'partial' | 'blocked';

export type ProposalCard = {
  id: number;
  title: string;
  character: string | null;
  status: ProposalStatus;
  latest_version_id: number;
  version_count: number;
  window: ProposalWindow;
  instruments: string[];
  moves: Move[];
  news_total: number;
  overlap_max: Overlap | null;
  check_status: CheckStatus;
  /**
   * Track A (2026-09-28). Absent or null on a card whose latest version was
   * built before the data check: its coverage is unknown, not complete.
   */
  data_status?: DataStatus | null;
  gap_count?: number;
  blockers?: number;
};

export const REJECT_REASONS = [
  'already_covered',
  'boring',
  'wrong_period',
  'weak_news',
  'other',
] as const;

export type RejectReason = (typeof REJECT_REASONS)[number];

export type VersionSummary = {
  id: number;
  number: number;
  created_at: IsoTimestamp;
  diff: string;
};

export type SaveStatus = 'saving' | 'saved' | 'failed';

/** An instrument the save left out because it cannot be traded. */
export type ExcludedInstrument = {
  code: string;
  reason: string;
};

export type SavedState = {
  status: SaveStatus;
  draft_id: number | null;
  blueprint_id: number | null;
  version_id: number | null;
  message: string;
  /** Set by a successful save; absent on saves from before Track B. */
  excluded_instruments?: ExcludedInstrument[];
  /** How many AI Curated news rows the saved blueprint serves. */
  curated_rows?: number;
};

export type Proposal = ProposalCard & {
  thread_id: number;
  versions: VersionSummary[];
  rejection: { reasons: RejectReason[]; note: string } | null;
  saved: SavedState | null;
};

export type RejectPayload = {
  reasons: RejectReason[];
  note?: string;
};

export type SavePayload = {
  version_id: number;
  trainee_title?: string;
  trainee_brief?: string;
};

export type SaveResponse = {
  proposal: Proposal;
  job: Job;
};

export type CurateResponse = { job: Job };

export type Budget = {
  spent_usd: number;
  cap_usd: number;
  calls: number;
  model: string;
  /** False where the server has the chat builder turned off; the spend fields are then absent. */
  enabled?: boolean;
};

/* ------------------------------------------------------------------ *
 * Long-term memories: what the builder was told to remember
 * ------------------------------------------------------------------ */

export type MemoryScope = 'user' | 'global';

export type Memory = {
  id: number;
  text: string;
  scope: MemoryScope;
  created_at: IsoTimestamp;
  /** The chat it was learnt in, when there was one. */
  source_thread_id: number | null;
  created_by: number | null;
};

export type MemoryList = { results: Memory[] };

/* ------------------------------------------------------------------ *
 * ProposalVersion: the preview
 * ------------------------------------------------------------------ */

export type BondIssue = {
  asset_id: number;
  name: string;
  first_landing: IsoDate | null;
  last_landing: IsoDate | null;
};

export type BondFamily = {
  code: string;
  /** null when the family does not resolve over the window (`ok` false). */
  series_id: number | null;
  issues: BondIssue[];
  roll_landings: IsoDate[];
  ok: boolean;
  error: string;
};

/**
 * What an instrument is. The builder takes any platform instrument, not only
 * EUR/USD and US/German bonds. Only a bond is analysed as a yield (moves in
 * bp, curve in % on the yield axis); every other kind, a kind this screen
 * does not know yet included, is a price that moves in percent. The string
 * fallback keeps a new kind from breaking the screens.
 */
export type InstrumentKind =
  | 'fx'
  | 'bond'
  | 'etf'
  | 'equity'
  | 'commodity'
  | 'crypto'
  | (string & {});

export type Instrument = {
  code: string;
  label: string;
  kind: InstrumentKind;
  /** 'bp' for a bond, '%' for every other kind. */
  unit: MoveUnit;
  /** null when PM lacks the analysis series: reported as a data gap, not a crash. */
  analysis_asset_id: number | null;
  graded_as: string | null;
  /** e.g. "up = EUR stronger", "up = yield up, price down" */
  direction_note: string;
  /** Bonds only: the synthetic family the blueprint trades. */
  family: BondFamily | null;
};

/**
 * One analysis curve. Bonds are yields in percent on the `yield` axis; every
 * other kind is a price on the `price` axis.
 */
export type CurveSeries = {
  code: string;
  label: string;
  axis: 'price' | 'yield';
  unit: 'price' | '%';
  values: Array<number | null>;
};

export type SpreadSeries = {
  code: string;
  label: string;
  unit: 'bp';
  values: Array<number | null>;
};

export type Curves = {
  dates: IsoDate[];
  series: CurveSeries[];
  spreads: SpreadSeries[];
};

export type TurnMove = {
  value: number | null;
  unit: MoveUnit;
  z: number | null;
  /** The date of the week's biggest daily move. */
  big_day: IsoDate | null;
};

export type NewsRole = 'driver' | 'context' | 'counter';
export type Sign = -1 | 0 | 1;

export type CurationStatus = 'pending' | 'accepted' | 'needs_review' | 'failed';

export type Curation = {
  status: CurationStatus;
  headline: string | null;
  reason: string;
};

export type NewsItem = {
  id: number;
  timestamp: IsoTimestamp;
  day: IsoDate;
  headline: string;
  excerpt: string;
  kind: string;
  role: NewsRole;
  concepts: string[];
  /** Keyed by instrument code: the headline's expected push. */
  signs: Record<string, Sign>;
  reasons: string[];
  score: number;
  curation: Curation | null;
};

export type Turn = {
  index: number;
  /** The Saturday after the previous landing. */
  start: IsoDate;
  /** This turn's landing (a Friday). */
  end: IsoDate;
  moves: Record<string, TurnMove>;
  news: NewsItem[];
  quota: number;
  available: number;
  thin: boolean;
};

export type PreviewCheck = {
  key: string;
  status: 'ok' | 'warn' | 'fail' | 'info';
  label: string;
  detail: string;
};

export type PregradeStatus = 'not_predicted' | 'pass' | 'warn' | 'fail';

/* ------------------------------------------------------------------ *
 * Data coverage (Track A): what the window's data can and cannot carry
 * ------------------------------------------------------------------ */

export type PriceCoverage = {
  status: 'ok' | 'partial' | 'missing';
  /** Bonds are read as yields, every other kind as prices. */
  unit: 'price' | 'yield';
  /** The landings (Fridays) with no close. */
  weeks_missing: IsoDate[];
  available_from: IsoDate | null;
  available_to: IsoDate | null;
};

export type NewsCoverage = {
  status: 'ok' | 'thin' | 'none';
  /** Turn indexes serving fewer than two items on this instrument's topics. */
  thin_turns: number[];
  /** Items on this instrument's topics the turn serves, one count per turn. */
  per_turn: number[];
  /**
   * What the archive offered on those topics, per turn. Absent on versions
   * built before 2026-09-28 (their `per_turn` counted the offer instead).
   */
  available_per_turn?: number[];
};

export type InstrumentCoverage = {
  code: string;
  label: string;
  prices: PriceCoverage;
  /** Registry concept, PM asset and (bonds) issues priced at every landing. */
  tradable: { ok: boolean; reason: string };
  graded: { ok: boolean; column: string | null; reason: string };
  news: NewsCoverage;
};

export type GapKind =
  | 'prices'
  | 'yields'
  | 'news'
  | 'tradable'
  | 'grading'
  | 'bond_issues'
  | 'archive';

/**
 * `blocker` only when saving would fail or mis-grade a TRADED instrument;
 * everything else (an untradable or ungraded instrument, thin news, an
 * analysis-only instrument's missing data) is a `warning`.
 */
export type GapSeverity = 'blocker' | 'warning';

/** Whose lane the remedy is in; the vocabulary of the pm.api docs. */
export type GapOwner = 'ours' | 'PM (Tudor)' | 'data';

export type Gap = {
  /** Null for a window-wide gap (the news archive). */
  code: string | null;
  kind: GapKind;
  severity: GapSeverity;
  turns: number[];
  detail: string;
  /** What to add, and where. */
  remedy: string;
  owner: GapOwner;
};

export type DataCoverage = {
  status: DataStatus;
  instruments: InstrumentCoverage[];
  gaps: Gap[];
};

export type Preview = {
  schema: 'builder-preview-v1';
  title: string;
  character: string | null;
  /** Admin-facing; hindsight allowed. */
  thesis: string;
  window: ProposalWindow & { landings: IsoDate[] };
  instruments: Instrument[];
  curves: Curves;
  turns: Turn[];
  news_counts: {
    total: number;
    driver: number;
    context: number;
    counter: number;
  };
  /** Includes `{key: 'data'}` once the preview carries `data_status`. */
  checks: PreviewCheck[];
  /**
   * Track A (2026-09-28). Absent on versions built before the data check:
   * the screens treat that as "unknown", never as complete.
   */
  data_status?: DataCoverage;
  overlap: Overlap[];
  pregrade: { status: PregradeStatus; detail: string };
  /** The only trainee-facing text: no dates, names or outcomes. */
  trainee: { title: string; brief: string; leaks: string[] };
  constraints: Constraints;
};

export type ProposalVersion = {
  id: number;
  proposal_id: number;
  number: number;
  created_at: IsoTimestamp;
  diff: string;
  preview: Preview;
};
